import { AiAssistFieldPatch } from '../../shared/ai-assist/ai-assist.models';
import { slugifyArticleTitle } from './growth-article-publishing.util';
import {
  GrowthContentAssetEditorState,
  GrowthContentAssetSeoModel,
  cloneGrowthContentAssetEditorState,
  normalizeGrowthContentBodyFormat
} from './growth-content-asset-editor.util';
import {
  GrowthContentAssistClientSnapshot,
  GrowthContentAssistDraftResponse
} from './growth-marketing.service';

export function buildGrowthContentAssistClientSnapshot(
  state: GrowthContentAssetEditorState
): GrowthContentAssistClientSnapshot {
  const model = state.model;
  return {
    title: model.title,
    summary: model.summary,
    body: model.bodyMarkdown,
    locale: model.locale,
    bodyFormat: model.bodyFormat,
    contentType: model.contentType
  };
}

/**
 * Must match GrowthContentAssistAction.ComputeSourceHash:
 * SHA256 hex of JSON `{ title, summary, body, locale, bodyFormat, version }`.
 */
export async function computeGrowthContentAssistSourceHash(
  snapshot: GrowthContentAssistClientSnapshot,
  version: number | null
): Promise<string> {
  const material = JSON.stringify({
    title: snapshot.title ?? null,
    summary: snapshot.summary ?? null,
    body: snapshot.body ?? null,
    locale: snapshot.locale ?? null,
    bodyFormat: snapshot.bodyFormat ?? null,
    version
  });
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(material));
  return Array.from(new Uint8Array(digest))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
}

export function mapGrowthContentAssistDraftToFieldPatches(
  response: GrowthContentAssistDraftResponse,
  beforeSnapshot: GrowthContentAssistClientSnapshot,
  currentSlug: string,
  seoBefore?: GrowthContentAssetSeoModel
): AiAssistFieldPatch[] {
  const seoSnapshot = seoBefore ?? {
    metaTitle: '',
    metaDescription: '',
    keywords: '',
    titleFa: '',
    titleEn: '',
    descriptionFa: '',
    descriptionEn: '',
    ogImage: ''
  };
  const patch = response.patch ?? {};
  const patches: AiAssistFieldPatch[] = [];

  if (patch.title != null && patch.title !== '') {
    patches.push({
      fieldKey: 'title',
      labelKey: 'growth.marketing.campaigns.fields.contentTitle',
      before: beforeSnapshot.title ?? '',
      after: patch.title
    });
  }
  const resolvedSlug = (patch.slug ?? '').trim() || (patch.title ? slugifyArticleTitle(patch.title) : '');
  if (resolvedSlug) {
    patches.push({
      fieldKey: 'slug',
      labelKey: 'growth.articlePublishing.fields.slug',
      before: currentSlug ?? '',
      after: resolvedSlug
    });
  }
  if (patch.summary != null && patch.summary !== '') {
    patches.push({
      fieldKey: 'summary',
      labelKey: 'growth.marketing.campaigns.fields.summary',
      before: beforeSnapshot.summary ?? '',
      after: patch.summary
    });
  }
  if (patch.bodyMarkdown != null && patch.bodyMarkdown !== '') {
    patches.push({
      fieldKey: 'body',
      labelKey: 'growth.contentAsset.fields.body',
      before: beforeSnapshot.body ?? '',
      after: patch.bodyMarkdown
    });
  }
  if (patch.bodyFormat != null && patch.bodyFormat !== '') {
    patches.push({
      fieldKey: 'bodyFormat',
      labelKey: 'growth.contentAsset.fields.bodyFormat',
      before: beforeSnapshot.bodyFormat ?? '',
      after: patch.bodyFormat
    });
  }
  if (patch.locale != null && patch.locale !== '') {
    patches.push({
      fieldKey: 'locale',
      labelKey: 'growth.marketing.campaigns.fields.locale',
      before: beforeSnapshot.locale ?? '',
      after: patch.locale
    });
  }

  const seo = patch.seo;
  if (seo) {
    if (seo.metaTitle != null && seo.metaTitle !== '') {
      patches.push({
        fieldKey: 'metaTitle',
        labelKey: 'growth.contentAsset.fields.metaTitle',
        before: seoSnapshot.metaTitle,
        after: seo.metaTitle
      });
    }
    if (seo.metaDescription != null && seo.metaDescription !== '') {
      patches.push({
        fieldKey: 'metaDescription',
        labelKey: 'growth.contentAsset.fields.metaDescription',
        before: seoSnapshot.metaDescription,
        after: seo.metaDescription
      });
    }
    if (seo.keywords?.length) {
      patches.push({
        fieldKey: 'keywords',
        labelKey: 'growth.contentAsset.fields.keywords',
        before: seoSnapshot.keywords,
        after: seo.keywords.join(', ')
      });
    }
    if (seo.titleFa != null && seo.titleFa !== '') {
      patches.push({
        fieldKey: 'titleFa',
        labelKey: 'growth.contentAsset.fields.titleFa',
        before: seoSnapshot.titleFa,
        after: seo.titleFa
      });
    }
    if (seo.titleEn != null && seo.titleEn !== '') {
      patches.push({
        fieldKey: 'titleEn',
        labelKey: 'growth.contentAsset.fields.titleEn',
        before: seoSnapshot.titleEn,
        after: seo.titleEn
      });
    }
    if (seo.descriptionFa != null && seo.descriptionFa !== '') {
      patches.push({
        fieldKey: 'descriptionFa',
        labelKey: 'growth.contentAsset.fields.descriptionFa',
        before: seoSnapshot.descriptionFa,
        after: seo.descriptionFa
      });
    }
    if (seo.descriptionEn != null && seo.descriptionEn !== '') {
      patches.push({
        fieldKey: 'descriptionEn',
        labelKey: 'growth.contentAsset.fields.descriptionEn',
        before: seoSnapshot.descriptionEn,
        after: seo.descriptionEn
      });
    }
    if (seo.ogImage != null && seo.ogImage !== '') {
      patches.push({
        fieldKey: 'ogImage',
        labelKey: 'growth.contentAsset.fields.ogImage',
        before: seoSnapshot.ogImage,
        after: seo.ogImage
      });
    }
  }

  const cover = patch.cover;
  if (cover?.imageUrl) {
    patches.push({
      fieldKey: 'cover',
      labelKey: 'growth.contentAsset.fields.ogImage',
      before: '',
      after: JSON.stringify({
        imageUrl: cover.imageUrl,
        altText: cover.altText ?? '',
        imagePrompt: cover.imagePrompt ?? '',
        aspectRatio: cover.aspectRatio ?? ''
      })
    });
    const ogFromSeo = (patch.seo?.ogImage ?? '').trim();
    if (!ogFromSeo && !(seoSnapshot.ogImage ?? '').trim()) {
      patches.push({
        fieldKey: 'ogImage',
        labelKey: 'growth.contentAsset.fields.ogImage',
        before: seoSnapshot.ogImage,
        after: cover.imageUrl
      });
    }
  }

  return patches;
}

export function applyGrowthContentAssistFieldPatches(
  state: GrowthContentAssetEditorState,
  patches: AiAssistFieldPatch[]
): GrowthContentAssetEditorState {
  const next = cloneGrowthContentAssetEditorState(state);
  const model = next.model;

  for (const patch of patches) {
    switch (patch.fieldKey) {
      case 'title':
        model.title = patch.after;
        break;
      case 'slug':
        if (!next.slugManuallyEdited || !model.slug.trim()) {
          model.slug = patch.after;
          next.slugManuallyEdited = false;
        }
        break;
      case 'summary':
        model.summary = patch.after;
        break;
      case 'body':
      case 'bodyMarkdown':
        model.bodyMarkdown = patch.after;
        break;
      case 'bodyFormat':
        model.bodyFormat = normalizeGrowthContentBodyFormat(patch.after);
        break;
      case 'locale':
        model.locale = patch.after;
        break;
      case 'metaTitle':
        next.seo.metaTitle = patch.after;
        break;
      case 'metaDescription':
        next.seo.metaDescription = patch.after;
        break;
      case 'keywords':
        next.seo.keywords = patch.after;
        break;
      case 'titleFa':
        next.seo.titleFa = patch.after;
        break;
      case 'titleEn':
        next.seo.titleEn = patch.after;
        break;
      case 'descriptionFa':
        next.seo.descriptionFa = patch.after;
        break;
      case 'descriptionEn':
        next.seo.descriptionEn = patch.after;
        break;
      case 'ogImage':
        next.seo.ogImage = patch.after;
        break;
      case 'cover':
        next.existingBodyJson = mergeCoverIntoGrowthContentBodyJson(
          next.existingBodyJson,
          parseCoverFieldPatchAfter(patch.after)
        );
        break;
      default:
        break;
    }
  }

  syncSlugFromTitleAfterAiAssist(next, patches);
  return next;
}

function parseCoverFieldPatchAfter(after: string): {
  imageUrl: string;
  altText?: string;
  imagePrompt?: string;
  aspectRatio?: string;
} {
  try {
    const parsed = JSON.parse(after) as {
      imageUrl?: string;
      altText?: string;
      imagePrompt?: string;
      aspectRatio?: string;
    };
    if (parsed?.imageUrl) {
      return {
        imageUrl: parsed.imageUrl,
        altText: parsed.altText,
        imagePrompt: parsed.imagePrompt,
        aspectRatio: parsed.aspectRatio
      };
    }
  } catch {
    // Legacy: after was plain image URL.
  }
  return { imageUrl: after };
}

function mergeCoverIntoGrowthContentBodyJson(
  existingBodyJson: string | null,
  cover: { imageUrl: string; altText?: string; imagePrompt?: string; aspectRatio?: string }
): string {
  let root: Record<string, unknown> = {};
  if (existingBodyJson?.trim()) {
    try {
      const parsed = JSON.parse(existingBodyJson) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        root = { ...parsed };
      }
    } catch {
      root = {};
    }
  }

  root['cover'] = {
    imageUrl: cover.imageUrl,
    ...(cover.altText ? { altText: cover.altText } : {}),
    ...(cover.imagePrompt ? { imagePrompt: cover.imagePrompt } : {}),
    ...(cover.aspectRatio ? { aspectRatio: cover.aspectRatio } : {})
  };

  return JSON.stringify(root);
}

function syncSlugFromTitleAfterAiAssist(
  state: GrowthContentAssetEditorState,
  patches: AiAssistFieldPatch[]
): void {
  const model = state.model;
  if (state.slugManuallyEdited && model.slug.trim()) {
    return;
  }

  const slugPatch = patches.find(item => item.fieldKey === 'slug' && item.after.trim());
  if (slugPatch) {
    model.slug = slugPatch.after.trim();
    return;
  }

  if (model.title.trim()) {
    model.slug = slugifyArticleTitle(model.title);
  }
}
